import type {
  ActionApprovalFilter,
  AiStepSort,
  DraftDisposition,
  DraftFilters,
  EchoTab,
} from "../types";
import type { ChipProps } from "@heroui/react";
import type { AgentStepStatus } from "@/features/ai-agent/types/agent-step";
import type { ReportInfoTipEntry } from "@/features/reports/common/components/report-info-tip";
import type {
  BreakdownBand,
  ReportFilterOption,
  ReportSeriesDefinition,
} from "@/features/reports/common/types";
import type {
  EchoBookedBy,
  EchoBookingCategory,
  EchoBookingOutcome,
  EchoMessagesBand,
  ReplyEditType,
  ReplyOutcome,
} from "@/types/reports";

import { REPLY_OUTCOME_COLOR } from "./labels";

import { AI_ASSISTANT } from "@/config/constants";
import {
  REPLY_REVIEW_REASON_LABEL,
  REPLY_REVIEW_REASONS,
} from "@/features/ai-agent/constants/draft-feedback";
import { REPORT_FILTER_ANY } from "@/features/reports/common/constants/list-filters";
import { CLINIC_SYSTEM_FALLBACK } from "@/features/reports/common/constants/report-labels";
import { SERIES_COLOR } from "@/utils/chart-color";

export const ECHO_REPORT_PATH = "/reports/ai";

/** Each is its own page, `/reports/ai/<id>`, in the sidebar's Echo section; the first opens by default. */
export const ECHO_TABS: { id: EchoTab; label: string; icon: string }[] = [
  { id: "bookings", label: "Bookings", icon: "solar:calendar-add-linear" },
  {
    id: "drafts",
    label: "Drafts & feedback",
    icon: "solar:pen-new-square-linear",
  },
  { id: "speed", label: "Speed & actions", icon: "solar:bolt-linear" },
  { id: "credits", label: "Credits & charges", icon: "solar:wallet-linear" },
];

export const ECHO_TAB_IDS: EchoTab[] = ECHO_TABS.map(({ id }) => id);

export const ECHO_CARD = {
  BOOKINGS: "echo_bookings",
  BOOKINGS_BY_CATEGORY: "echo_bookings_by_category",
  BOOKINGS_FIGURES: "echo_bookings_figures",
  DRAFT_OUTCOMES: "echo_draft_outcomes",
  DRAFT_TREND: "echo_draft_trend",
  DRAFT_REASONS: "echo_draft_reasons",
  DRAFT_EDITS: "echo_draft_edits",
  CLOCKS: "echo_clocks",
  CREDIT_ACTIONS: "echo_credit_actions",
  CREDITS_FIGURES: "echo_credits_figures",
  BOOKING_EVIDENCE: "echo_booking_evidence",
  REVIEW_EVIDENCE: "echo_review_evidence",
  ACTION_EXECUTION: "echo_action_execution",
  CREDIT_LEDGER: "echo_credit_ledger",
} as const;

export const ECHO_BOOKING_CATEGORIES: EchoBookingCategory[] = [
  "ai_led",
  "ai_assisted",
  "human_led",
  "crm_led",
  "unclassified",
];

export const BOOKING_CATEGORY_LABEL: Record<EchoBookingCategory, string> = {
  ai_led: `Booked by ${AI_ASSISTANT.name}`,
  ai_assisted: `Booked with ${AI_ASSISTANT.name}'s help`,
  human_led: "Booked by your team",
  crm_led: `Booked in ${CLINIC_SYSTEM_FALLBACK}`,
  unclassified: "Not classified",
};

export const BOOKING_CATEGORY_CHIP_COLOR: Record<
  EchoBookingCategory,
  ChipProps["color"]
> = {
  ai_led: "accent",
  ai_assisted: "accent",
  human_led: "default",
  crm_led: "default",
  unclassified: "default",
};

export const WITH_ECHO_CATEGORIES: EchoBookingCategory[] = [
  "ai_led",
  "ai_assisted",
];

/** The chart's two groups; the evidence opens on the first. */
export const ECHO_BOOKING_BANDS: BreakdownBand[] = [
  { label: `With ${AI_ASSISTANT.name}`, keys: WITH_ECHO_CATEGORIES },
  { label: `Without ${AI_ASSISTANT.name}`, keys: ["human_led", "crm_led"] },
];

/** `?category=` for every booking, since no value opens on Echo's own. */
export const ALL_BOOKING_CATEGORIES = "all";

/** Echo's two in its own colour, the rest in greys. */
export const BOOKING_CATEGORY_COLOR: Record<EchoBookingCategory, string> = {
  ai_led: "var(--echo)",
  ai_assisted: "color-mix(in oklab, var(--echo) 50%, var(--surface))",
  human_led: "var(--muted)",
  crm_led: "color-mix(in oklab, var(--muted) 45%, var(--surface))",
  unclassified: "var(--border)",
};

export const BOOKING_OUTCOMES: EchoBookingOutcome[] = [
  "completed",
  "no_show",
  "cancelled",
  "confirmed",
  "pending",
  "not_recorded",
];

/** A confirmed or unconfirmed visit whose time has passed, on the filter and on the row. */
export const OUTCOME_NOT_RECORDED_LABEL = "Outcome not recorded";

const BOOKING_OUTCOME_LABEL: Record<EchoBookingOutcome, string> = {
  completed: "Attended",
  no_show: "No-shows",
  cancelled: "Cancellations",
  confirmed: "Confirmed",
  pending: "Unconfirmed",
  not_recorded: OUTCOME_NOT_RECORDED_LABEL,
};

export const BOOKING_OUTCOME_OPTIONS: ReportFilterOption[] = [
  { key: REPORT_FILTER_ANY, label: "Any outcome" },
  ...BOOKING_OUTCOMES.map((key) => ({
    key,
    label: BOOKING_OUTCOME_LABEL[key],
  })),
];

export const ECHO_BOOKED_BY: EchoBookedBy[] = ["ai", "user", "crm"];

export const ECHO_MESSAGES_BANDS: EchoMessagesBand[] = [
  "no_conversation",
  "none",
  "below_threshold",
  "at_threshold",
];

/** Who completed a booking, for a booking actor the row names no person for. */
export const BOOKED_BY_LABEL = {
  crm: CLINIC_SYSTEM_FALLBACK,
  user: "A teammate",
  unknown: "Not recorded",
} as const;

export const DRAFT_DISPOSITIONS: DraftDisposition[] = [
  "sent",
  "edited",
  "steered",
  "discarded",
  "pending",
  "expired",
];

export const DRAFT_DISPOSITION_LABEL: Record<DraftDisposition, string> = {
  sent: "Sent as is",
  edited: "Edited, then sent",
  steered: `Sent back to ${AI_ASSISTANT.name}`,
  discarded: "Discarded",
  pending: "Waiting for review",
  expired: "Expired",
};

export const DRAFT_DISPOSITION_METHODOLOGY: Record<DraftDisposition, string> = {
  sent: `A teammate sent ${AI_ASSISTANT.name}'s draft without changing a word.`,
  edited: "A teammate changed the draft, then sent it.",
  steered: `A teammate asked ${AI_ASSISTANT.name} to redo it with a note. The redo counts as its own draft.`,
  discarded:
    "A teammate threw the draft away or wrote their own reply instead.",
  pending:
    "Nobody has acted on it yet and the conversation is still open. Waiting now, on Speed & actions, counts every waiting draft whatever the dates.",
  expired: `The conversation closed with the draft still unsent. Drafts ${AI_ASSISTANT.name} replaced with a newer one, because the customer wrote again or a teammate asked, are not counted.`,
};

export const DRAFT_DISPOSITION_TERMS: ReportInfoTipEntry[] =
  DRAFT_DISPOSITIONS.map((key) => ({
    term: DRAFT_DISPOSITION_LABEL[key],
    explains: DRAFT_DISPOSITION_METHODOLOGY[key],
  }));

export const DRAFT_DISPOSITION_COLOR: Record<DraftDisposition, string> = {
  sent: REPLY_OUTCOME_COLOR.sent,
  edited: REPLY_OUTCOME_COLOR.edited_sent,
  steered: REPLY_OUTCOME_COLOR.steered,
  discarded: REPLY_OUTCOME_COLOR.discarded,
  pending: "var(--warning)",
  expired: SERIES_COLOR.neutral,
};

export const DRAFT_CARD_TITLE = {
  OUTCOMES: `What happened to ${AI_ASSISTANT.name}'s drafts`,
  TREND: "Outcome mix over time",
  REASONS: "Why drafts were turned down",
  EDITS: "How much teammates changed",
} as const;

/** The trend stacks each bucket's shares, bottom-up in the bar's order. */
export const DRAFT_TREND_SERIES: ReportSeriesDefinition[] =
  DRAFT_DISPOSITIONS.map((key) => ({
    key,
    label: DRAFT_DISPOSITION_LABEL[key],
    color: DRAFT_DISPOSITION_COLOR[key],
    format: (value: number) => `${Math.round(value)}%`,
  }));

/** The reply outcomes behind each settled disposition, for the list's filter. */
export const DRAFT_DISPOSITION_OUTCOMES: Record<
  "sent" | "edited" | "steered" | "discarded",
  ReplyOutcome[]
> = {
  sent: ["sent"],
  edited: ["edited_sent"],
  steered: ["steered"],
  discarded: ["discarded", "taken_over"],
};

/** The settled disposition each outcome counts under. */
export const DISPOSITION_OF_OUTCOME = Object.fromEntries(
  Object.entries(DRAFT_DISPOSITION_OUTCOMES).flatMap(
    ([disposition, outcomes]) =>
      outcomes.map((outcome) => [outcome, disposition]),
  ),
) as Partial<Record<ReplyOutcome, DraftDisposition>>;

/** The drafts the reasons card reads: every way a person turned one down. */
export const DRAFT_TURNED_DOWN_OUTCOMES: ReplyOutcome[] = [
  "discarded",
  "steered",
  "taken_over",
];

/** The dispositions a reason can come with: a reason is given when turning a draft down. */
export const DRAFT_REASON_DISPOSITIONS: DraftDisposition[] = [
  "steered",
  "discarded",
];

/** The dispositions nobody settled, so no teammate is on them. */
export const DRAFT_UNSETTLED_DISPOSITIONS: DraftDisposition[] = [
  "pending",
  "expired",
];

/** A review list nobody has narrowed. */
export const NO_DRAFT_FILTERS: DraftFilters = {};

export const DRAFT_FILTER_PARAMS = [
  "disposition",
  "reason",
  "edit",
  "settledBy",
] as const;

/** Lightest change first. */
export const DRAFT_EDIT_TYPES: ReplyEditType[] = [
  "trimmed",
  "bubbles_removed",
  "reworded",
  "rewritten",
];

export const DRAFT_EDIT_LABEL: Record<ReplyEditType, string> = {
  trimmed: "Shortened",
  bubbles_removed: "Dropped a bubble",
  reworded: "Reworded",
  rewritten: "Rewritten",
};

export const DRAFT_EDIT_DESCRIPTION: Record<ReplyEditType, string> = {
  trimmed: `Cut down, mostly ${AI_ASSISTANT.name}'s own words`,
  bubbles_removed: "Removed one of the messages",
  reworded: "Some of the words changed",
  rewritten: "Mostly the teammate's own words",
};

/** One hue, stronger as the edit grows, so a heavier change reads as more. */
export const DRAFT_EDIT_COLOR: Record<ReplyEditType, string> = {
  trimmed: "color-mix(in oklch, var(--accent) 35%, var(--surface))",
  bubbles_removed: "color-mix(in oklch, var(--accent) 55%, var(--surface))",
  reworded: "color-mix(in oklch, var(--accent) 78%, var(--surface))",
  rewritten: "var(--accent)",
};

export const DRAFT_DISPOSITION_OPTIONS: ReportFilterOption[] = [
  { key: REPORT_FILTER_ANY, label: "Any outcome" },
  ...DRAFT_DISPOSITIONS.map((key) => ({
    key,
    label: DRAFT_DISPOSITION_LABEL[key],
  })),
];

export const DRAFT_REASON_OPTIONS: ReportFilterOption[] = [
  { key: REPORT_FILTER_ANY, label: "Any reason" },
  ...REPLY_REVIEW_REASONS.map((key) => ({
    key,
    label: REPLY_REVIEW_REASON_LABEL[key],
  })),
];

export const DRAFT_EDIT_OPTIONS: ReportFilterOption[] = [
  { key: REPORT_FILTER_ANY, label: "Any change" },
  ...DRAFT_EDIT_TYPES.map((key) => ({ key, label: DRAFT_EDIT_LABEL[key] })),
];

/** A tool call's result. Keys are step statuses. */
export const STEP_RESULT_LABEL: Record<AgentStepStatus, string> = {
  executed: "Done",
  failed: "Failed",
  denied: "Declined",
  waiting: "Waiting for approval",
  approved: "Approved",
  running: "Running",
  sending: "Sending",
  deferred: "Runs after the reply",
  superseded: "Replaced",
  resolved: "Done",
  answered: "Answered",
};

export const STEP_RESULT_COLOR: Partial<
  Record<AgentStepStatus, ChipProps["color"]>
> = {
  executed: "success",
  resolved: "success",
  failed: "danger",
  denied: "warning",
  waiting: "warning",
};

/** The run step type the action table lists: every tool call. */
export const ECHO_ACTION_STEP_TYPE = "tool";

export const ECHO_ACTION_DEFAULT_SORT: AiStepSort = {
  sortBy: "createdAt",
  sortOrder: "desc",
};

export const ECHO_EVIDENCE_TABLE_HEIGHT = "28rem";

/** The server's cap on one booking evidence file (`ECHO_BOOKINGS_EXPORT_LIMIT`). */
export const ECHO_BOOKINGS_EXPORT_LIMIT = 5000;

/** The `ownerId` the backend reads as "Echo ran it without asking anyone". */
export const ECHO_STEP_OWNER = "echo";

/** The backend's name for the charges with no action on them, on the bar and in the ledger's filter. */
export const CREDIT_ACTION_OTHER = "other";

/** The tabs that count Echo's own runs, where "nothing in these dates" can be said of Echo. */
export const ECHO_RUN_TABS: EchoTab[] = ["drafts", "speed"];

export const ACTION_APPROVAL_OPTIONS: ReportFilterOption<ActionApprovalFilter>[] =
  [
    { key: REPORT_FILTER_ANY, label: "Any approval" },
    { key: "not_required", label: "Not required" },
    { key: "required", label: "Required" },
    { key: "approved", label: "Approved" },
    { key: "denied", label: "Declined" },
    { key: "awaiting", label: "Waiting" },
  ];

export const ACTION_RESULT_OPTIONS: ReportFilterOption[] = [
  { key: REPORT_FILTER_ANY, label: "Any result" },
  { key: "executed", label: STEP_RESULT_LABEL.executed },
  { key: "failed", label: STEP_RESULT_LABEL.failed },
  { key: "denied", label: STEP_RESULT_LABEL.denied },
  { key: "waiting", label: STEP_RESULT_LABEL.waiting },
];
